import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import type { GcpDiskSnapshotProperties } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import {
  convertFromBytes,
  convertToBytes,
  fileSizeFormatter,
} from '@/utils/fileSizeFormatter';

import { getValueInRange, SliderWithInput } from './sliderWithInput';

import { Loader } from '../layout/loading';
import { TransactionalTextField } from '../shared/transactionalTextField';

export const diskPresets: Record<
  string,
  {
    displayName: string;
    minSize: number;
    maxSize: number;
    minIOPS?: number;
    maxIOPS?: number;
    minThroughput?: number;
    maxThroughput?: number;
  }
> = {
  'pd-standard': {
    displayName: 'Standard Persistent Disk',
    minSize: 1,
    maxSize: 65536,
  },
  'pd-balanced': {
    displayName: 'Balanced Persistent Disk',
    minSize: 1,
    maxSize: 65536,
  },
  'pd-ssd': {
    displayName: 'SSD Persistent Disk',
    minSize: 1,
    maxSize: 65536,
  },
  'pd-extreme': {
    displayName: 'Extreme Persistent Disk',
    minSize: 1,
    maxSize: 65536,
    minIOPS: 1,
    maxIOPS: 120000,
  },
  regional_pd: {
    // this?
    displayName: 'Regional Persistent Disk',
    minSize: 200,
    maxSize: 65536,
  },
  'hyperdisk-balanced': {
    displayName: 'Hyperdisk Balanced',
    minSize: 4,
    maxSize: 65536,
    minIOPS: 3000,
    maxIOPS: 160_000,
    minThroughput: 1,
    maxThroughput: 2400,
  },
  'hyperdisk-balanced-ha': {
    displayName: 'Hyperdisk Balanced High Availability',
    minSize: 4,
    maxSize: 65536,
    minIOPS: 3000,
    maxIOPS: 100_000,
    minThroughput: 140,
    maxThroughput: 1200,
  },
  'hyperdisk-balanced-high-availability': {
    displayName: 'Hyperdisk Balanced High Availability',
    minSize: 4,
    maxSize: 65536,
    minIOPS: 3000,
    maxIOPS: 100_000,
    minThroughput: 140,
    maxThroughput: 1200,
  },
  'hyperdisk-extreme': {
    displayName: 'Hyperdisk Extreme',
    minSize: 4,
    maxSize: 65536,
    minIOPS: 16000,
    maxIOPS: 1_000_000,
  },
  'hyperdisk-ml': {
    displayName: 'Hyperdisk ML',
    minSize: 4,
    maxSize: 65536,
    minThroughput: 10,
    maxThroughput: 20_000,
  },
  'hyperdisk-throughput': {
    displayName: 'Hyperdisk Throughput',
    minSize: 4,
    maxSize: 65536,
    minThroughput: 10,
    maxThroughput: 7124,
  },
};

interface Props {
  accountId: string;
  zone: string;
  machineType?: string;
  initialSettings?: GcpDiskSnapshotProperties;
  settings?: GcpDiskSnapshotProperties;
  onChange: (settings: GcpDiskSnapshotProperties) => void;
  keepOriginalSettings?: boolean;
  setKeepOriginalSettings: (value: boolean) => void;
}

export const DiskSettingsSection = ({
  accountId,
  zone,
  machineType,
  initialSettings,
  settings: _settings,
  keepOriginalSettings,
  onChange,
  setKeepOriginalSettings,
}: Props) => {
  const dal = useDAL();
  const { data: body } = dal.restore.gcp.useListDiskTypes(
    accountId,
    zone,
    machineType
  );

  if (!initialSettings) {
    return <Loader />;
  }

  const settings = _settings || initialSettings;
  const preset = diskPresets[settings.diskType];

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Box className='flex justify-between items-center'>
        <Typography className='font-semibold' variant='body1'>
          Keep original settings
        </Typography>
        <Switch
          size='small'
          disabled={!preset}
          checked={keepOriginalSettings}
          onChange={() => {
            setKeepOriginalSettings(!keepOriginalSettings);
          }}
        />
      </Box>
      {keepOriginalSettings && (
        <Box className='mt-[10px]'>
          <Table size='small'>
            <TableBody>
              <TableRow>
                <TableCell
                  className='border-0 px-0'
                  sx={{ color: 'var(--mui-palette-text-disabled)' }}
                >
                  Name
                </TableCell>
                <TableCell
                  className='border-0 px-0'
                  sx={{ color: 'var(--mui-palette-text-disabled)' }}
                >
                  {initialSettings?.diskName}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className='border-0 px-0'
                  sx={{ color: 'var(--mui-palette-text-disabled)' }}
                >
                  Type
                </TableCell>
                <TableCell
                  className='border-0 px-0'
                  sx={{ color: 'var(--mui-palette-text-disabled)' }}
                >
                  {initialSettings?.diskType}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className='border-0 px-0'
                  sx={{ color: 'var(--mui-palette-text-disabled)' }}
                >
                  Size
                </TableCell>
                <TableCell
                  className='border-0 px-0'
                  sx={{ color: 'var(--mui-palette-text-disabled)' }}
                >
                  {fileSizeFormatter(initialSettings?.diskSizeBytes, 1, false)}
                </TableCell>
              </TableRow>
              {initialSettings.iops && (
                <TableRow>
                  <TableCell
                    className='border-0 px-0'
                    sx={{ color: 'var(--mui-palette-text-disabled)' }}
                  >
                    IOPS
                  </TableCell>
                  <TableCell
                    className='border-0 px-0'
                    sx={{ color: 'var(--mui-palette-text-disabled)' }}
                  >
                    {initialSettings.iops}
                  </TableCell>
                </TableRow>
              )}
              {initialSettings.throughput && (
                <TableRow>
                  <TableCell
                    className='border-0 px-0'
                    sx={{ color: 'var(--mui-palette-text-disabled)' }}
                  >
                    Throughput
                  </TableCell>
                  <TableCell
                    className='border-0 px-0'
                    sx={{ color: 'var(--mui-palette-text-disabled)' }}
                  >{`${initialSettings.throughput} MB/s`}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      {!keepOriginalSettings && (
        <Box className='mt-[20px] w-1/2 pr-[18px]'>
          <FormControl size='small' className='w-full mb-[24px]'>
            <FormLabel>Disk Name</FormLabel>
            <TransactionalTextField
              initValue={settings?.diskName ?? ''}
              onChange={(diskName) => onChange({ ...settings, diskName })}
            />
          </FormControl>
          <FormControl size='small' className='w-full mb-[24px]'>
            <FormLabel>Disk Type</FormLabel>
            <Select
              value={settings.diskType}
              onChange={(e) => {
                const newType = e.target.value;
                const newPreset = diskPresets[newType];

                onChange({
                  ...settings,
                  diskType: newType,
                  diskSizeBytes: settings.diskSizeBytes,
                  iops:
                    newPreset.minIOPS && newPreset.maxIOPS
                      ? getValueInRange(
                          settings.iops || initialSettings.iops,
                          newPreset.minIOPS,
                          newPreset.maxIOPS
                        )
                      : undefined,
                  throughput:
                    newPreset.minThroughput && newPreset.maxThroughput
                      ? getValueInRange(
                          settings.throughput || initialSettings.throughput,
                          newPreset.minThroughput,
                          newPreset.maxThroughput
                        )
                      : undefined,
                });
              }}
            >
              {Object.entries(diskPresets).map(
                ([key, value]) =>
                  body?.diskTypes?.includes(key) && (
                    <MenuItem key={key} value={key}>
                      {value.displayName}
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
          <FormControl size='small' className='w-full mb-[24px]'>
            <SliderWithInput
              id='size'
              title='Size (GiB)'
              value={convertFromBytes(settings.diskSizeBytes, 'GiB')}
              minValue={convertFromBytes(initialSettings?.diskSizeBytes, 'GiB')}
              maxValue={preset.maxSize}
              onChange={(value) =>
                onChange({
                  ...settings,
                  diskSizeBytes: convertToBytes(value, 'GiB'),
                })
              }
            />
          </FormControl>
          {preset.minIOPS && preset.maxIOPS && (
            <FormControl size='small' className='w-full mb-[24px]'>
              <SliderWithInput
                id='iops'
                title='IOPS'
                value={settings.iops}
                minValue={preset.minIOPS}
                maxValue={preset.maxIOPS}
                onChange={(value) => onChange({ ...settings, iops: value })}
              />
            </FormControl>
          )}
          {preset.minThroughput && preset.maxThroughput && (
            <FormControl size='small' className='w-full mb-[24px]'>
              <SliderWithInput
                id='throughput'
                title='Throughput (MB/s)'
                value={settings.throughput}
                minValue={preset.minThroughput}
                maxValue={preset.maxThroughput}
                onChange={(value) =>
                  onChange({ ...settings, throughput: value })
                }
              />
            </FormControl>
          )}
        </Box>
      )}
    </Box>
  );
};
